import { db, functions } from '../firebaseConfig';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc,
  setDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where,
  arrayUnion,
  arrayRemove,
  serverTimestamp 
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';

/**
 * Organization Service
 * Handles all organization/team-related operations
 */

// Default permissions for each role
export const DEFAULT_PERMISSIONS = {
  owner: {
    proposals: { create: true, edit: true, view: true, delete: true },
    contracts: { create: true, edit: true, view: true, delete: true },
    templates: { create: true, edit: true, view: true, delete: true },
    examples: { create: true, edit: true, view: true, delete: true },
    caseStudies: { create: true, edit: true, view: true, delete: true },
    taskManager: { access: true },
    organization: { manage: true, invite: true, removeMember: true, editPermissions: true },
  },
  admin: {
    proposals: { create: true, edit: true, view: true, delete: true },
    contracts: { create: true, edit: true, view: true, delete: true },
    templates: { create: true, edit: true, view: true, delete: true },
    examples: { create: true, edit: true, view: true, delete: true },
    caseStudies: { create: true, edit: true, view: true, delete: true },
    taskManager: { access: true },
    organization: { manage: false, invite: true, removeMember: false, editPermissions: true },
  },
  member: {
    proposals: { create: true, edit: false, view: true, delete: false },
    contracts: { create: true, edit: false, view: true, delete: false },
    templates: { create: false, edit: false, view: true, delete: false },
    examples: { create: false, edit: false, view: true, delete: false },
    caseStudies: { create: false, edit: false, view: true, delete: false },
    taskManager: { access: true },
    organization: { manage: false, invite: false, removeMember: false, editPermissions: false },
  },
};

// Create a new organization
export const createOrganization = async (name, ownerId, ownerEmail) => {
  try {
    const orgRef = doc(collection(db, 'organizations'));
    const orgData = {
      id: orgRef.id,
      name,
      ownerId,
      members: [{
        userId: ownerId,
        email: ownerEmail,
        role: 'owner',
        joinedAt: new Date().toISOString()
      }],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };
    
    await setDoc(orgRef, orgData);
    
    // Update user's organizations list
    const userRef = doc(db, 'users', ownerId);
    await updateDoc(userRef, {
      organizations: arrayUnion(orgRef.id),
      currentOrganization: orgRef.id
    });
    
    return { ...orgData, createdAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
  } catch (error) {
    console.error('Error creating organization:', error);
    throw error;
  }
};

// Get organization by ID
export const getOrganization = async (orgId) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);
    
    if (orgSnap.exists()) {
      return { id: orgSnap.id, ...orgSnap.data() };
    }
    return null;
  } catch (error) {
    console.error('Error getting organization:', error);
    throw error;
  }
};

// Get all organizations for a user
export const getUserOrganizations = async (userId) => {
  try {
    const orgsQuery = query(
      collection(db, 'organizations'),
      where('members', 'array-contains-any', [{ userId }])
    );
    
    // Fallback: get all orgs and filter manually
    const allOrgsSnap = await getDocs(collection(db, 'organizations'));
    const userOrgs = [];
    
    allOrgsSnap.forEach((doc) => {
      const data = doc.data();
      if (data.members && data.members.some(m => m.userId === userId)) {
        userOrgs.push({ id: doc.id, ...data });
      }
    });
    
    return userOrgs;
  } catch (error) {
    console.error('Error getting user organizations:', error);
    throw error;
  }
};

// Update organization
export const updateOrganization = async (orgId, updates) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    await updateDoc(orgRef, {
      ...updates,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating organization:', error);
    throw error;
  }
};

// Add member to organization
export const addMemberToOrganization = async (orgId, userId, userEmail, role = 'member') => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const memberData = {
      userId,
      email: userEmail,
      role,
      joinedAt: new Date().toISOString()
    };
    
    await updateDoc(orgRef, {
      members: arrayUnion(memberData),
      updatedAt: serverTimestamp()
    });
    
    // Update user's organizations list
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      organizations: arrayUnion(orgId)
    });
  } catch (error) {
    console.error('Error adding member:', error);
    throw error;
  }
};

// Remove member from organization
export const removeMemberFromOrganization = async (orgId, userId) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);
    
    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }
    
    const orgData = orgSnap.data();
    const memberToRemove = orgData.members.find(m => m.userId === userId);
    
    if (memberToRemove) {
      await updateDoc(orgRef, {
        members: arrayRemove(memberToRemove),
        updatedAt: serverTimestamp()
      });
      
      // Update user's organizations list
      const userRef = doc(db, 'users', userId);
      await updateDoc(userRef, {
        organizations: arrayRemove(orgId)
      });
    }
  } catch (error) {
    console.error('Error removing member:', error);
    throw error;
  }
};

// Create invitation
export const createInvitation = async (orgId, orgName, inviterEmail, inviteeEmail, role = 'member') => {
  try {
    const inviteRef = doc(collection(db, 'invitations'));
    const now = new Date().toISOString();
    const inviteData = {
      id: inviteRef.id,
      organizationId: orgId,
      organizationName: orgName,
      inviterEmail,
      inviteeEmail,
      role,
      status: 'pending',
      createdAt: now,
      expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString() // 7 days
    };
    
    await setDoc(inviteRef, inviteData);
    
    // Manually trigger email sending (since Firestore trigger may not work in emulator without Firestore emulator)
    let emailSent = false;
    let emailError = null;
    try {
      console.log('📧 Calling sendInvitationEmailManual function...');
      const sendEmail = httpsCallable(functions, 'sendInvitationEmailManual');
      const result = await sendEmail({ invitationId: inviteRef.id });
      console.log('✅ Email function result:', result.data);
      emailSent = true;
    } catch (error) {
      console.error('⚠️ Error sending email (invitation still created):', error);
      emailError = error.message;
      // Don't throw - invitation was created successfully, email is secondary
    }
    
    return { 
      ...inviteData, 
      emailSent,
      emailError
    };
  } catch (error) {
    console.error('Error creating invitation:', error);
    throw error;
  }
};

// Get pending invitations for a user
export const getUserInvitations = async (userEmail) => {
  try {
    const invitesQuery = query(
      collection(db, 'invitations'),
      where('inviteeEmail', '==', userEmail),
      where('status', '==', 'pending')
    );
    
    const snapshot = await getDocs(invitesQuery);
    const invitations = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      // Check if not expired
      if (new Date(data.expiresAt) > new Date()) {
        invitations.push({ id: doc.id, ...data });
      }
    });
    
    return invitations;
  } catch (error) {
    console.error('Error getting invitations:', error);
    throw error;
  }
};

// Accept invitation
export const acceptInvitation = async (invitationId, userId, userEmail) => {
  try {
    const inviteRef = doc(db, 'invitations', invitationId);
    const inviteSnap = await getDoc(inviteRef);
    
    if (!inviteSnap.exists()) {
      throw new Error('Invitation not found');
    }
    
    const invite = inviteSnap.data();
    
    // Add user to organization
    await addMemberToOrganization(invite.organizationId, userId, userEmail, invite.role);
    
    // Update invitation status
    await updateDoc(inviteRef, {
      status: 'accepted',
      acceptedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error accepting invitation:', error);
    throw error;
  }
};

// Decline invitation (deletes it)
export const declineInvitation = async (invitationId) => {
  try {
    const inviteRef = doc(db, 'invitations', invitationId);
    await deleteDoc(inviteRef);
  } catch (error) {
    console.error('Error declining invitation:', error);
    throw error;
  }
};

// Delete organization (owner only)
export const deleteOrganization = async (orgId) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    await deleteDoc(orgRef);
  } catch (error) {
    console.error('Error deleting organization:', error);
    throw error;
  }
};

// Check if user is organization owner
export const isOrganizationOwner = (organization, userId) => {
  return organization && organization.ownerId === userId;
};

// Check if user is organization admin
export const isOrganizationAdmin = (organization, userId) => {
  if (!organization || !organization.members) return false;
  const member = organization.members.find(m => m.userId === userId);
  return member && (member.role === 'owner' || member.role === 'admin');
};

// Get user's current organization
export const getUserCurrentOrganization = async (userId) => {
  try {
    const userRef = doc(db, 'users', userId);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      if (userData.currentOrganization) {
        return await getOrganization(userData.currentOrganization);
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting current organization:', error);
    throw error;
  }
};

// Switch user's current organization
export const switchOrganization = async (userId, orgId) => {
  try {
    const userRef = doc(db, 'users', userId);
    await updateDoc(userRef, {
      currentOrganization: orgId
    });
  } catch (error) {
    console.error('Error switching organization:', error);
    throw error;
  }
};

// Import personal templates and examples into organization
export const importPersonalDataToOrganization = async (userId, orgId) => {
  try {
    // Get personal templates
    const personalTemplatesRef = collection(db, 'users', userId, 'proposal-templates');
    const templatesSnap = await getDocs(personalTemplatesRef);
    
    // Get personal examples
    const personalExamplesRef = collection(db, 'users', userId, 'project-examples');
    const examplesSnap = await getDocs(personalExamplesRef);
    
    console.log('Import Debug:', {
      personalTemplatesCount: templatesSnap.docs.length,
      personalExamplesCount: examplesSnap.docs.length,
      userId,
      orgId
    });
    
    let templatesImported = 0;
    let examplesImported = 0;
    
    // Import templates
    const orgTemplatesRef = collection(db, 'organizations', orgId, 'proposal-templates');
    for (const doc of templatesSnap.docs) {
      const data = doc.data();
      await addDoc(orgTemplatesRef, {
        ...data,
        organizationId: orgId,
        importedFrom: 'personal',
        importedAt: serverTimestamp(),
        createdAt: data.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      templatesImported++;
    }
    
    // Import examples
    const orgExamplesRef = collection(db, 'organizations', orgId, 'project-examples');
    for (const doc of examplesSnap.docs) {
      const data = doc.data();
      await addDoc(orgExamplesRef, {
        ...data,
        organizationId: orgId,
        importedFrom: 'personal',
        importedAt: serverTimestamp(),
        createdAt: data.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      examplesImported++;
    }
    
    return {
      templatesImported,
      examplesImported,
      personalTemplatesFound: templatesSnap.docs.length,
      personalExamplesFound: examplesSnap.docs.length,
      success: true
    };
  } catch (error) {
    console.error('Error importing personal data:', error);
    throw error;
  }
};

// Get member's permissions (combines role-based and custom permissions)
export const getMemberPermissions = (organization, userId) => {
  if (!organization || !organization.members) return null;
  
  const member = organization.members.find(m => m.userId === userId);
  if (!member) return null;
  
  // Start with role-based permissions
  const rolePermissions = DEFAULT_PERMISSIONS[member.role] || DEFAULT_PERMISSIONS.member;
  
  // Override with custom permissions if they exist
  if (member.customPermissions) {
    return {
      ...rolePermissions,
      ...member.customPermissions
    };
  }
  
  return rolePermissions;
};

// Check if user has specific permission
export const hasPermission = (organization, userId, feature, action) => {
  const permissions = getMemberPermissions(organization, userId);
  if (!permissions) return false;
  
  if (!permissions[feature]) return false;
  
  return permissions[feature][action] === true;
};

// Update member's custom permissions
export const updateMemberPermissions = async (orgId, userId, customPermissions) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);
    
    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }
    
    const orgData = orgSnap.data();
    const updatedMembers = orgData.members.map(member => {
      if (member.userId === userId) {
        return {
          ...member,
          customPermissions
        };
      }
      return member;
    });
    
    await updateDoc(orgRef, {
      members: updatedMembers,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating member permissions:', error);
    throw error;
  }
};

// Update member's role
export const updateMemberRole = async (orgId, userId, newRole) => {
  try {
    const orgRef = doc(db, 'organizations', orgId);
    const orgSnap = await getDoc(orgRef);
    
    if (!orgSnap.exists()) {
      throw new Error('Organization not found');
    }
    
    const orgData = orgSnap.data();
    const updatedMembers = orgData.members.map(member => {
      if (member.userId === userId) {
        return {
          ...member,
          role: newRole,
          // Clear custom permissions when changing role
          customPermissions: undefined
        };
      }
      return member;
    });
    
    await updateDoc(orgRef, {
      members: updatedMembers,
      updatedAt: new Date().toISOString()
    });
  } catch (error) {
    console.error('Error updating member role:', error);
    throw error;
  }
};

